import { message } from "antd";
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import type { RolePermOpenParams } from "@/components/RoleDetailModal";
import { RoleDetailModal } from "@/components/RoleDetailModal";
import { AccountDetailModal } from "@/pages/Accounts/components/AccountDetail";
import { AgentDetailModal } from "@/pages/Agents/components/AgentDetailModal";
import { EmployeeDetailModal } from "@/pages/Employees/components/EmployeeDetailModal";
import { EmployeeRoleAssignModal } from "@/pages/Employees/components/EmployeeRoleAssignModal";
import type { EmployeeRoleAssignTarget } from "@/pages/Employees/hooks/useEmployeeRoleAssignModal";
import { EntityDetailModal } from "@/pages/EntitiesPage/components/EntityDetailModal";
import { MemberDetailModal } from "@/pages/Members/components/MemberDetailModal";
import { StoreDetailModal } from "@/pages/Stores/components/StoreDetailModal";

import { RelatedDetailContext } from "./detailNavigationContext";
import type {
  EmployeesListRefreshHandler,
  RelatedDetailContextValue,
  RelatedDetailStackItem,
  RelatedEntityDetailKind,
  StoreListHandler,
} from "./types";

/** 空 ID 时的提示文案 */
const EMPTY_ID_MESSAGE: Record<RelatedEntityDetailKind, string> = {
  agent: "未关联代理",
  store: "未关联门店",
  entity: "未关联工商实体",
  account: "未关联账号",
  employee: "未关联员工",
  member: "未关联会员",
};

/** 校验 ID 后入栈打开详情 */
const pushDetail = (
  kind: RelatedEntityDetailKind,
  id: string | null | undefined,
  setStack: Dispatch<SetStateAction<RelatedDetailStackItem[]>>,
) => {
  const trimmed = id?.trim();
  if (!trimmed) {
    message.warning(EMPTY_ID_MESSAGE[kind]);
    return;
  }
  setStack((prev) => [...prev, { kind, id: trimmed }]);
};

/** 渲染栈顶详情 Modal */
const RelatedDetailHost = ({
  stack,
  onClose,
  nav,
  onOpenStoreListFallback,
  roleAssignTarget,
  roleRefreshKey,
  onOpenRoleAssign,
  onCloseRoleAssign,
  onRoleAssignSuccess,
}: {
  stack: RelatedDetailStackItem[];
  onClose: () => void;
  nav: RelatedDetailContextValue;
  onOpenStoreListFallback: (agentId: string) => void;
  roleAssignTarget: EmployeeRoleAssignTarget | null;
  roleRefreshKey: number;
  onOpenRoleAssign: (target: EmployeeRoleAssignTarget) => void;
  onCloseRoleAssign: () => void;
  onRoleAssignSuccess: () => void;
}) => {
  const top = stack[stack.length - 1];
  if (!top) return null;

  switch (top.kind) {
    case "store":
      return (
        <StoreDetailModal
          open
          shopId={top.id}
          onClose={onClose}
          onOpenAgent={nav.openAgent}
          onOpenStore={nav.openStore}
          onOpenEntity={nav.openEntity}
          onOpenAccount={nav.openAccount}
        />
      );
    case "agent":
      return (
        <AgentDetailModal
          open
          agentId={top.id}
          onClose={onClose}
          onOpenAccount={nav.openAccount}
          onOpenEntity={nav.openEntity}
          onOpenStoreList={onOpenStoreListFallback}
        />
      );
    case "entity":
      return (
        <EntityDetailModal
          open
          entityId={top.id}
          onClose={onClose}
          showFooter={false}
          onClickShop={nav.openStore}
          onClickAgent={nav.openAgent}
          onClickAccount={nav.openAccount}
        />
      );
    case "account":
      return <AccountDetailModal open onClose={onClose} id={top.id} />;
    case "employee":
      return (
        <EmployeeDetailModal
          open
          employeeId={top.id}
          onClose={onClose}
          onOpenRole={nav.openRolePerm}
          onOpenAccount={nav.openAccount}
          onOpenStore={nav.openStore}
        />
      );
    case "member":
      return (
        <MemberDetailModal
          open
          memberId={top.id}
          onClose={onClose}
          onOpenStore={nav.openStore}
          onOpenAccount={nav.openAccount}
        />
      );
    case "role": {
      const employeeSource = top.source.mode === "employee" ? top.source : null;
      return (
        <>
          <RoleDetailModal
            open
            roleName={top.roleName}
            source={top.source}
            employeeDisplayName={top.employeeDisplayName}
            refreshKey={roleRefreshKey}
            onAddRole={
              employeeSource
                ? () =>
                    onOpenRoleAssign({
                      employeeId: employeeSource.employeeId,
                      orgId: employeeSource.orgId,
                      employeeName: top.employeeDisplayName,
                      employeeNo: top.employeeNo,
                    })
                : undefined
            }
            onClose={() => {
              onCloseRoleAssign();
              onClose();
            }}
          />
          {roleAssignTarget ? (
            <EmployeeRoleAssignModal
              open
              target={roleAssignTarget}
              onClose={onCloseRoleAssign}
              onSuccess={onRoleAssignSuccess}
            />
          ) : null}
        </>
      );
    }
    default:
      return null;
  }
};

/** 跨实体详情 Provider：包裹业务页并挂载全局详情 Host */
export const RelatedDetailProvider = ({ children }: { children: ReactNode }) => {
  // 详情栈，支持 A → B 逐层打开
  const [stack, setStack] = useState<RelatedDetailStackItem[]>([]);
  // 代理页注册的「下辖门店列表」回调
  const storeListHandlerRef = useRef<StoreListHandler | null>(null);
  /** 员工页注册的列表刷新（角色分配成功后） */
  const employeesListRefreshRef = useRef<EmployeesListRefreshHandler | null>(null);
  /** 员工角色分配弹窗目标（挂在角色详情之下） */
  const [roleAssignTarget, setRoleAssignTarget] = useState<EmployeeRoleAssignTarget | null>(null);
  /** 角色详情刷新键（分配成功后递增） */
  const [roleRefreshKey, setRoleRefreshKey] = useState(0);

  /** 关闭栈顶一层 */
  const close = useCallback(() => {
    setRoleAssignTarget(null);
    setStack((prev) => prev.slice(0, -1));
  }, []);

  /** 清空详情栈 */
  const closeAll = useCallback(() => {
    setRoleAssignTarget(null);
    setStack([]);
  }, []);

  /** 打开角色权限详情（入栈） */
  const openRolePerm = useCallback((params: RolePermOpenParams) => {
    setStack((prev) => [
      ...prev,
      {
        kind: "role",
        roleName: params.roleName,
        source: params.source,
        employeeDisplayName: params.employeeDisplayName,
        employeeNo: params.employeeNo,
      },
    ]);
  }, []);

  /** 打开员工角色分配 */
  const onOpenRoleAssign = useCallback((target: EmployeeRoleAssignTarget) => {
    setRoleAssignTarget(target);
  }, []);

  /** 关闭员工角色分配 */
  const onCloseRoleAssign = useCallback(() => {
    setRoleAssignTarget(null);
  }, []);

  /** 角色分配成功：关闭穿梭、刷新角色 Tab，并通知员工列表 */
  const onRoleAssignSuccess = useCallback(() => {
    setRoleAssignTarget(null);
    setRoleRefreshKey((key) => key + 1);
    employeesListRefreshRef.current?.();
  }, []);

  /** 代理页注册/注销下辖门店列表打开器 */
  const registerStoreListHandler = useCallback((handler: StoreListHandler | null) => {
    storeListHandlerRef.current = handler;
  }, []);

  /** 员工页注册/注销列表刷新 */
  const registerEmployeesListRefreshHandler = useCallback(
    (handler: EmployeesListRefreshHandler | null) => {
      employeesListRefreshRef.current = handler;
    },
    [],
  );

  /** 下辖门店：优先走代理页注册回调，否则提示 */
  const onOpenStoreListFallback = useCallback((agentId: string) => {
    if (storeListHandlerRef.current) {
      storeListHandlerRef.current(agentId);
      return;
    }
    message.info("下辖门店列表请从代理管理页查看");
  }, []);

  const nav = useMemo<RelatedDetailContextValue>(
    () => ({
      openAgent: (id) => pushDetail("agent", id, setStack),
      openStore: (id) => pushDetail("store", id, setStack),
      openEntity: (id) => pushDetail("entity", id, setStack),
      openAccount: (id) => pushDetail("account", id, setStack),
      openEmployee: (id) => pushDetail("employee", id, setStack),
      openMember: (id) => pushDetail("member", id, setStack),
      openRolePerm,
      close,
      closeAll,
      registerStoreListHandler,
      registerEmployeesListRefreshHandler,
    }),
    [close, closeAll, openRolePerm, registerStoreListHandler, registerEmployeesListRefreshHandler],
  );

  return (
    <RelatedDetailContext.Provider value={nav}>
      {children}
      <RelatedDetailHost
        stack={stack}
        onClose={close}
        nav={nav}
        onOpenStoreListFallback={onOpenStoreListFallback}
        roleAssignTarget={roleAssignTarget}
        roleRefreshKey={roleRefreshKey}
        onOpenRoleAssign={onOpenRoleAssign}
        onCloseRoleAssign={onCloseRoleAssign}
        onRoleAssignSuccess={onRoleAssignSuccess}
      />
    </RelatedDetailContext.Provider>
  );
};
